import { Box, styled } from '@mui/material';
import { mergeRefs } from 'Components/designSystem';
import { FeedbackIconButton } from 'features/feedback';
import React, { forwardRef, memo, useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ContentCard } from 'shared/components';
import { APP_ROUTES } from 'shared/constants';
import { useTenantContext } from 'shared/contexts';
import { useEnteredViewport } from 'shared/hooks';

import { useViewActivitySender } from '../../hooks';
import { ListOffer, ProcessedListOffer } from '../../types';
import OfferDescription from '../OfferDescription';
import ProcessedOfferStatus from '../OfferDescription/ProcessedOfferStatus';
import Controls from './Controls';
import ProcessedOfferFooter from './ProcessedOfferFooter';

interface OfferListItemProps {
    offer: ListOffer | ProcessedListOffer;
    onOpen: (orderId: string, orderRoleId: string) => void;
    onReject: (offer: ListOffer) => void;
    onFeedbackClick: (offer: ListOffer) => void;
}

const OfferListItem = forwardRef<HTMLDivElement, OfferListItemProps>((props, ref) => {
    const {
        offer,
        onOpen,
        onReject,
        onFeedbackClick
    } = props;

    const tenant = useTenantContext();
    const navigate = useNavigate();

    const {
        orderId,
        orderRoleId,
        status
    } = offer;

    const [ node, setNode ] = useState<HTMLElement | null>(null);

    const mergedRef = mergeRefs([ setNode, ref ]);

    const enteredViewport = useEnteredViewport({
        element: node,
        options: { threshold: 1 }
    });

    useViewActivitySender({
        orderId,
        offerToken: '',
        enteredViewport,
        /**
         * As we don't have activity in order right now
         * this hook temporary disabled.
         */
        disabled: true
    });

    const handleOpenClick = useCallback(() => {
        onOpen(orderId, orderRoleId);
    }, [ onOpen, orderId, orderRoleId ]);

    const handleDeclineClick = useCallback(() => {
        onReject(offer);
    }, [ onReject, offer ]);

    const handleFeedbackClick = useCallback(() => {
        onFeedbackClick?.(offer);
    }, [ onFeedbackClick, offer ]);

    const jobDetailsPath = APP_ROUTES.jobDetails(`${ orderId }|${ orderRoleId }`);

    const handleViewDetailsClick = useCallback(() => {
        navigate(jobDetailsPath);
    }, [ navigate, jobDetailsPath ]);

    const isProcessed = Boolean(status);
    const hasRole = Boolean(offer.role);
    const feedbackIcon = <FeedbackIconButton onClick={ handleFeedbackClick } />;

    return (
        <ContentCard ref={ mergedRef }
            sx={{ position: 'relative' }}
        >
            { !hasRole && (
                <Corner>
                    <ProcessedOfferStatus status={ status } />
                    { feedbackIcon }
                </Corner>
            ) }

            <OfferDescription { ...offer }
                status={ hasRole ? status : undefined }
                timeZone={ tenant.timeZone }
                headerAdornment={ hasRole ? feedbackIcon : undefined } />

            { isProcessed ? (
                <ProcessedOfferFooter onViewDetailsClick={ handleViewDetailsClick }
                    jobDetailsPath={ jobDetailsPath } />
            ) : (
                <Controls
                    onOpenClick={ handleOpenClick }
                    onDeclineClick={ handleDeclineClick }
                />
            ) }
        </ContentCard>
    );
});

const Corner = styled(Box)(({ theme }) => ({
    position: 'absolute',
    top: theme.spacing(5),
    right: theme.spacing(5),
    display: 'flex',
    alignItems: 'center',
    columnGap: theme.spacing(1)
}));

OfferListItem.displayName = 'OfferListItem';

export default memo(OfferListItem);
